import { Injectable, OnInit } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GeneratePdfService } from './generate-pdf-service';
import { SurveyResponse } from 'src/app/create/models/serveyResponses.model';
import { Observable, Subject, of, forkJoin } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Constants } from '../constants';
import { NgxSpinnerService } from 'ngx-spinner';
import { BaseHttpService } from './basehttp.service';
import { IExcelOrExportDataObject } from '../models/excelOrExportDataObject.model';
import { QuestionViewModel } from '../models/questionview.model';
import { DatePipe } from '@angular/common';
import { NotificationService } from './notifications.service';
import { ExcelService } from './excel.service';
import { UtilityService } from './utility.service';
import { StorageService } from './token-storage.service';
import { AppConstants } from '../appconstants';
import { DisputeService } from './dispute.service';
import { IDispute } from '../models/dispute/dispute.model';
import { IDisputeQuestion } from '../models/dispute/disputeQuestion.model';

@Injectable({
    providedIn: 'root'
})

export class DownloadAsService {

    constant: Constants = new Constants();
    appConstants: AppConstants = new AppConstants();
    masterSurveyResults: SurveyResponse;
    surveyName: string;
    consentQuestions: QuestionViewModel[] = [];


    constructor(private http: BaseHttpService, private spinnerService: NgxSpinnerService,
        private generatePdf: GeneratePdfService, private notificationService: NotificationService,
        private excelService: ExcelService, private utilityService: UtilityService, private storage: StorageService,
        private disputeService: DisputeService) { }

    getSurveyResponseData(surveyId: string, surveyResponseIds: number[] = []): Observable<SurveyResponse[]> {
        const subject = new Subject<SurveyResponse[]>();
        const payload = {
            surveyId: surveyId,
            surveyResponseIds: surveyResponseIds || []
        };
        this.http.post(this.constant.getSurveyUserResponseData, payload).subscribe((result: SurveyResponse[]) => {
            subject.next(result);
        });
        return subject.asObservable();
    }
    
    downloadFile(surveyId: string, downloadType: string, incidentIds: number[]): Observable<boolean> {
        if (incidentIds == null || incidentIds.length == 0) {
            this.notificationService.showError("Please select at least one user to download the business process responses.", "Error!");
            return new Observable(observer => {
                observer.next(false);
                observer.complete();
            });
        }

        this.spinnerService.show();
        return new Observable(observer => {
            this.getSurveyResponseData(surveyId, incidentIds).subscribe((result: any) => {
                this.masterSurveyResults = result;
                this.masterSurveyResults.surveyName = result.surveyDetail?.name;
                this.surveyName = this.masterSurveyResults.surveyName;
                this.initiateDownload(downloadType, incidentIds,surveyId,result);
                this.spinnerService.hide();
                observer.next(true);
                observer.complete();
            }, error => {
                this.spinnerService.hide();
                observer.next(false);
                observer.complete();
            });
        });
    }
    initiateDownload(downloadType: string, incidentIds: number[],surveyId:any,surveyResults:any) {
        // Get user's local browser timezone
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timestamp = this.utilityService.getFilenameTimestamp('en-US', userTimeZone);

        this.getConsentQuestions();

        if (downloadType === 'excel') {
            const fileName = `Response_Result_${this.masterSurveyResults.surveyName}_${timestamp}`;
            const header = this.mapCommonHeader();
            this.mapQuestionHeader(header, incidentIds);
            const data = this.mapQuestionData(header, incidentIds);
            this.downloadExcel(fileName, header, data);
        }
        else if (downloadType === 'attestationpdf') {
            if (this.consentQuestions.length === 0) {
                this.notificationService.showInfo("No Attestations available for download.", "Info!");
            }
            else {
                
                this.mapAttestationData(incidentIds, true,surveyId,surveyResults).subscribe();
            }
        }
    }
    mapCommonHeader(): IExcelOrExportDataObject[] {
        let headerData: IExcelOrExportDataObject[] = [];
        let firstEntry: IExcelOrExportDataObject = { headerText: "UserName/ Question(s)", subheaders: [], questionId: 0, questionType: "", subQuestions: [] }
        let agencyIdEntry: IExcelOrExportDataObject = { headerText: "Agency ID", subheaders: [], questionId: 0, questionType: "", subQuestions: [] }
        let agencyNameEntry: IExcelOrExportDataObject = { headerText: "Agency Name", subheaders: [], questionId: 0, questionType: "", subQuestions: [] }
        headerData.push(firstEntry);
        headerData.push(agencyIdEntry);
        headerData.push(agencyNameEntry);
        return headerData;
    }
    mapResolutionExportHeader(): IExcelOrExportDataObject[] {
        let responseType: IExcelOrExportDataObject = { headerText: "Response Type", subheaders: [], questionId: 0, questionType: "", subQuestions: [] }
        let headerData: IExcelOrExportDataObject[] = [];
        let firstEntry: IExcelOrExportDataObject = { headerText: "UserName/ Question(s)", subheaders: [], questionId: 0, questionType: "", subQuestions: [] }
        let agencyIdEntry: IExcelOrExportDataObject = { headerText: "Agency ID", subheaders: [], questionId: 0, questionType: "", subQuestions: [] }
        let agencyNameEntry: IExcelOrExportDataObject = { headerText: "Agency Name", subheaders: [], questionId: 0, questionType: "", subQuestions: [] }
        headerData.push(responseType);
        headerData.push(firstEntry);
        headerData.push(agencyIdEntry);
        headerData.push(agencyNameEntry);
        return headerData;
    }
    mapQuestionHeader(headerData: IExcelOrExportDataObject[], incidentIds: number[]): IExcelOrExportDataObject[] {
        const orderedSections = this.getSectionsInWorkflowOrder();
        const allQuestions = this.getAllQuestionsFromSections(orderedSections);
        const userResponses = this.masterSurveyResults?.userResponses?.filter(x => incidentIds.includes(Number(x.surveyUserResponseRel))) || [];

        orderedSections.forEach(sec => {
            // Filter out displaytext, sign types and conditionally hidden questions
          let filterQuestions = sec.questions.filter(ques => ques.inputType != 'displaytext' && ques.inputType != 'sign');
            // Apply conditional visibility filter (checks section visibility first, then question visibility)
            filterQuestions = this.filterVisibleQuestions(filterQuestions, sec, allQuestions, userResponses);
            if (filterQuestions.length > 0) {
                filterQuestions.sort((a, b) => a.sequenceNumber - b.sequenceNumber).forEach(quesEntry => {
                    if (quesEntry.questionId != undefined) {
                        if (quesEntry.subQuestions && quesEntry.subQuestions.length == 0) {
                            headerData.push({
                                headerText: quesEntry.questionText, subheaders: [],
                                questionId: quesEntry.questionId, questionType: quesEntry.inputType, subQuestions: []
                            });
                        }
                        else {
                            /* Sub questions mostly available only for table/Grid question type */
                            const { cols, subQuesArray } = this.collectSubHeaders(quesEntry);

                            headerData.push({
                                headerText: quesEntry.questionText,
                                subheaders: cols,
                                questionId: quesEntry.questionId,
                                questionType: quesEntry.inputType,
                                subQuestions: subQuesArray
                            });
                        }
                    }
                });
            }
        })
        return headerData;
    }
    mapHeaderForConsent(headerData: IExcelOrExportDataObject[]): IExcelOrExportDataObject[] {
        if (this.consentQuestions.length === 0) {
            return;
        }

        this.consentQuestions.forEach(quesEntry => {
            if (quesEntry.questionId != undefined) {
                if (quesEntry.subQuestions && quesEntry.subQuestions.length == 0) {
                    headerData.push({
                        headerText: quesEntry.questionText, subheaders: [],
                        questionId: quesEntry.questionId, questionType: quesEntry.inputType, subQuestions: []
                    });
                }
                else {
                    /* Sub questions mostly available only for table/Grid question type */
                    const { cols, subQuesArray } = this.collectSubHeaders(quesEntry);
                    headerData.push({
                        headerText: quesEntry.questionText,
                        subheaders: cols,
                        questionId: quesEntry.questionId,
                        questionType: quesEntry.inputType,
                        subQuestions: subQuesArray
                    });
                }
            }
        });
        return headerData;
    }
    collectSubHeaders(mainQuestion: any): { cols: string[], subQuesArray: IExcelOrExportDataObject[] } {
        let cols: string[] = [];
        let subQuesArray: IExcelOrExportDataObject[] = [];

        mainQuestion.subQuestions.sort((a, b) => a.sequenceNumber - b.sequenceNumber).forEach(subQ => {
            if (subQ.subQuestions && subQ.subQuestions.length > 0) {
                subQ.subQuestions.forEach((childSubQ, idx) => {
                    cols.push(`${subQ.questionText} : ${mainQuestion.options[idx]?.value ?? ''}`);
                    subQuesArray.push(childSubQ);
                });
            } else {
                cols.push(subQ.questionText);
                subQuesArray.push({
                    questionId: subQ.questionId,
                    questionType: subQ.inputType
                } as IExcelOrExportDataObject);
            }
        });
        return { cols, subQuesArray };
    }

    private getAllQuestionsFromSections(sections: any[]): any[] {
        const allQuestions: any[] = [];
        sections.forEach(sec => {
            if (sec.questions && sec.questions.length > 0) {
                allQuestions.push(...sec.questions);
            }
        });
        return allQuestions;
    }

   
    private extractResponseValues(responseObj: any): string[] {
        let values: string[] = [];

        // First check responseText
        if (responseObj.responseText && responseObj.responseText.trim().length > 0) {
            values = responseObj.responseText.split('||').map((v: string) => v.trim()).filter((v: string) => v);
        }
        // If responseText is empty, check responses array (for checkbox/grid questions)
        else if (responseObj.responses && Array.isArray(responseObj.responses) && responseObj.responses.length > 0) {
            values = responseObj.responses.map((v: string) => v.trim()).filter((v: string) => v);
        }

        return values;
    }

   
    private checkParentResponseMatch(parentQuestion: any, requiredResponse: string, userResponses: any[]): boolean {
        for (const userResponse of userResponses) {
            const parentResponseObj = userResponse.responses?.find(r => r.questionId === parentQuestion.questionId);
            if (parentResponseObj) {
                const parentRespValues = this.extractResponseValues(parentResponseObj);
                const requiredValues = (requiredResponse || '').split('||').map((v: string) => v.trim()).filter((v: string) => v);
                if (parentRespValues.some((v: string) => requiredValues.includes(v))) {
                    return true;
                }
            }
        }
        return false;
    }

    
    private isSectionVisible(section: any, allQuestions: any[], userResponses: any[]): boolean {
        // No parent question dependency = always visible
        if (!section.parentQuestionGuid && !section.parentSectionGuid) {
            return true;
        }

        // No conditional question dependency = visible
        if (!section.parentQuesResponse) {
            return true;
        }

        // Find the parent question that controls this section's visibility
        const parentQuestion = allQuestions.find(q => q.questionGuid === section.parentQuestionGuid);
        if (!parentQuestion) {
            return true;
        }

        // Check if parent question's response matches required value
        return this.checkParentResponseMatch(parentQuestion, section.parentQuesResponse, userResponses);
    }

    private isQuestionVisible(question: any, allQuestions: any[], userResponses: any[]): boolean {
        // No parent = always visible (top-level question)
        if (!question.parentQuestionGuid) {
            return true;
        }

        // Find parent question
        const parentQuestion = allQuestions.find(q => q.questionGuid === question.parentQuestionGuid);
        if (!parentQuestion) {
            return true;
        }

        // First check if the parent itself is visible (recursive check)
        if (!this.isQuestionVisible(parentQuestion, allQuestions, userResponses)) {
            return false;
        }

        // Check if parent question's response matches required value
        return this.checkParentResponseMatch(parentQuestion, question.parentQuesResponse, userResponses);
    }

  
    private filterVisibleQuestions(questions: any[], section: any, allQuestions: any[], userResponses: any[]): any[] {
        
        // First check if the section itself is visible
        if (!this.isSectionVisible(section, allQuestions, userResponses)) {
            // Section is not visible, return empty array (no questions from this section)
            return [];
        }

        // Section is visible, now filter questions based on question-level conditional visibility
        return questions.filter(ques => this.isQuestionVisible(ques, allQuestions, userResponses));
    }
    getConsentQuestions() {
        this.consentQuestions = [];
        this.masterSurveyResults.surveyDetail.sections.forEach(sec => {
            let secConsentQue = sec.questions.filter(ques => ques.isConsentQuestion == true);
            this.consentQuestions.push(...secConsentQue);
        })
    }
    mapCommonData(userResponse: any,addResponseHeader:boolean=false,isResolutionExport:boolean=false): any[] {
        let data: any[] = [];
        if(addResponseHeader){
            isResolutionExport? data.push('Resolution'): data.push('Normal');//adding response type//adding response type
        }
        data.push(`${userResponse.firstName ? userResponse.firstName : ""} ${userResponse.lastName ? userResponse.lastName : ""}`);
        const agencyId = userResponse.agencyId ? userResponse.agencyId : ""
        const agencyName = userResponse.agencyName ? userResponse.agencyName : ""
        data.push(agencyId);
        data.push(agencyName);
        return data;
    }
    mapQuestionData(header: IExcelOrExportDataObject[], incidentIds: number[],isResolutionExport:boolean=false): any {
        const selectedResponses = this.masterSurveyResults.userResponses.filter(x => incidentIds.includes(Number(x.surveyUserResponseRel)));
        const allRespData = [];
        selectedResponses.forEach((user, index) => {
            let userQuestionAnswer = this.mapCommonData(user,isResolutionExport);
            const indexToExclude=userQuestionAnswer.length-1
            const quesHeaderList = header.filter((x, ind) => ind > indexToExclude);
            quesHeaderList.forEach(header => {
                if (header.subheaders.length > 0) {
                    header.subQuestions.forEach(subheader => {
                        const response = this.getFormattedResponse(user, subheader.questionId, subheader.questionType);
                        userQuestionAnswer.push(response);
                    });
                } else {
                    const response = this.getFormattedResponse(user, header.questionId, header.questionType);
                    userQuestionAnswer.push(response);
                }
            });
            allRespData.push(userQuestionAnswer);
        });
        return allRespData;
    }
    private getFormattedResponse(user: any, questionId: number, questionType: string): string {
        const userResponse = user.responses.find(response => response.questionId === questionId);
        if (!userResponse) {
            return "";
        }
        switch (questionType) {
            case 'fileupload': return this.formatFileUploadResponse(userResponse.responseText);
            case 'sign': return this.formatSignatureResponse(userResponse.responseText);
            default: return this.formatDefaultResponse(userResponse);
        }
    }
    private formatFileUploadResponse(responseText: string): string {
        if (!responseText) return "";
        try {
            const azureDet = JSON.parse(responseText);
            if (azureDet["Files"] && Array.isArray(azureDet["Files"])) {
                const fileNames = azureDet["Files"].map(elem => elem.Name).filter(name => name);
                if (fileNames.length > 0) {
                    return fileNames.join(",");
                }
            }
            if (Array.isArray(azureDet) && azureDet.length > 0) {
                const fileNames = azureDet.map(elem => elem.name).filter(name => name);
                if (fileNames.length > 0) {
                    return fileNames.join(",");
                }
            }
            
            return "";
        } catch {
            return "";
        }
    }
    private formatSignatureResponse(responseText: string): string {
        if (!responseText) return "";
        try {
            const signDet = JSON.parse(responseText);
            return signDet["value"] || "";
        } catch {
            return "";
        }
    }
    private formatDefaultResponse(userResponse: any): string {
        return userResponse.responses && userResponse.responses.length > 0
            ? userResponse.responses.join(", ")
            : userResponse.responseText || "";
    }
    mapAttestationData(incidentIds: number[],downloadLocally: boolean = true, surveyId?: string,surveyResults?:any): Observable<any[]> {
        // If we already have data just generate immediately
        if (surveyResults && surveyResults?.userResponses) {
            this.masterSurveyResults = surveyResults;
            this.masterSurveyResults.surveyName = surveyResults.surveyDetail?.name;
            this.getConsentQuestions();
            return of(this.callGeneratePdfForAttestation(downloadLocally,incidentIds));
        }

        if (!surveyId) {
            return of([]);
        }

        return this.getSurveyResponseData(surveyId, incidentIds).pipe(
            map((result: any) => {
                this.masterSurveyResults = result;
                this.masterSurveyResults.surveyName = result.surveyDetail?.name;
                this.getConsentQuestions();
                return this.callGeneratePdfForAttestation( downloadLocally,incidentIds);
            })
        );
    }
              

    callGeneratePdfForAttestation(downloadLocally: boolean, incidentIds:any[]): any[] {
        const results: any[] = [];

         // Filter sections that have consent questions
        const consentQuestionSections = this.masterSurveyResults.surveyDetail.sections.filter(sec => this.consentQuestions.some(q => q.sectionId === sec.sectionId));
        const allQuestions = this.getAllQuestionsFromSections(consentQuestionSections);
        const userResponses = this.masterSurveyResults?.userResponses?.filter(x => incidentIds.includes(Number(x.surveyUserResponseRel))) || [];
        const providerType = this.storage.get('LoggedInProviderType');
               
        for (const user of userResponses) {
            // Filter consent questions for this specific user
            let visibleConsentQuestions: QuestionViewModel[] = [];
            consentQuestionSections.forEach(section => {
                const sectionConsentQues = this.consentQuestions.filter(q => q.sectionId === section.sectionId);
                // Reuse existing filterVisibleQuestions method with single user
                const visible = this.filterVisibleQuestions(sectionConsentQues, section, allQuestions, [user]);
                visibleConsentQuestions.push(...visible);
            });
            
            // Sort filtered questions in workflow order
            const orderedQuestions = this.sortQuestionsInWorkflowOrder(visibleConsentQuestions);
            const sortedSignedQuestions = orderedQuestions.filter(q => q.inputType === 'sign');
            const sortedNonSignedQuestions = orderedQuestions.filter(q => q.inputType !== 'sign');
            
            const agencyId = user.agencyId ? user.agencyId : "";
            const agencyName = user.agencyName ? user.agencyName : "";
            const surveyResponseId = Number(user.surveyUserResponseRel);

            const pdfData = this.generatePdf.generateAttestationPdf(
                user,
                sortedNonSignedQuestions,
                sortedSignedQuestions,
                this.masterSurveyResults.surveyName,
                agencyName,
                agencyId.toString(),
                downloadLocally,
                providerType
            );

            // When not downloading locally, accumulate structured entries including responseId
            if (!downloadLocally && pdfData) {
                results.push({
                    pdfData,
                    incidentId: surveyResponseId,
                    agencyId: agencyId,
                    agencyName: agencyName
                });
            }
        }

        return results;
    }

    sortQuestionsInWorkflowOrder(consentQuestions : QuestionViewModel[]) : QuestionViewModel[]{
        let orderedQuestions = [] as QuestionViewModel[]; let wfDetNotAvailable: boolean = false;
        const workflow = this.masterSurveyResults.surveyWorkflow;
        if(workflow && workflow.workflowSteps && workflow.workflowSteps.length > 0) {
            for(const step of workflow.workflowSteps) {
                if(step.workflowStepSections && step.workflowStepSections.length > 0){
                    step.workflowStepSections.sort((a, b) => a.sequenceNumber - b.sequenceNumber).forEach(section => {
                        let foundSecQues = consentQuestions.filter(q => q.sectionId === section.sectionId).sort((a, b) => a.sequenceNumber - b.sequenceNumber);
                        orderedQuestions.push(...foundSecQues);
                    });
                }
                else {
                    wfDetNotAvailable = true;  
                }            
            }
        }
        else {
            wfDetNotAvailable = true;            
        }

        if(wfDetNotAvailable){
            orderedQuestions = [...this.consentQuestions];
        }

        return orderedQuestions;
    }

    getSectionsInWorkflowOrder(): any[] {
        let orderedSections: any[] = [];
        let wfDetNotAvailable: boolean = false;
        const workflow = this.masterSurveyResults.surveyWorkflow;
        
        if(workflow && workflow.workflowSteps && workflow.workflowSteps.length > 0) {
            for(const step of workflow.workflowSteps) {
                if(step.workflowStepSections && step.workflowStepSections.length > 0){
                    step.workflowStepSections.sort((a, b) => a.sequenceNumber - b.sequenceNumber).forEach(workflowSection => {
                        // Find the corresponding section from surveyDetail.sections
                        let foundSection = this.masterSurveyResults.surveyDetail.sections.find(s => s.sectionId === workflowSection.sectionId);
                        if(foundSection) {
                            orderedSections.push(foundSection);
                        }
                    });
                }
                else {
                    wfDetNotAvailable = true;  
                }            
            }
        }
        else {
            wfDetNotAvailable = true;            
        }

        if(wfDetNotAvailable){
            orderedSections = [...this.masterSurveyResults.surveyDetail.sections];
        }

        return orderedSections;
    }

    downloadExcel(fileName: string, header: IExcelOrExportDataObject[], data: any[]) {
        this.excelService.fileName = fileName;
        this.excelService.sheetName = this.surveyName;
        this.excelService.title = this.surveyName;
        this.excelService.header = [];
        this.excelService.header = header;
        this.excelService.data = [];
        this.excelService.data = data;
        this.excelService.generateExcelNew();
    }

    /**
     * Generates the Excel file as a Blob for upload (not downloaded locally).
     * Returns an Observable with the blob and fileName.
     */
    generateExcelForUpload(surveyId: string, incidentIds: number[]): Observable<{ blob: Blob, fileName: string }> {
        return new Observable(observer => {
            this.getSurveyResponseData(surveyId, incidentIds).subscribe({
                next: (result: any) => {
                    this.masterSurveyResults = result;
                    this.masterSurveyResults.surveyName = result.surveyDetail?.name;
                    this.surveyName = this.masterSurveyResults.surveyName;
                    this.getConsentQuestions();

                    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    const timestamp = this.utilityService.getFilenameTimestamp('en-US', userTimeZone);
                    const fileName = `Response_Result_${this.masterSurveyResults.surveyName}_${timestamp}`;

                    const header = this.mapCommonHeader();
                    this.mapQuestionHeader(header, incidentIds);
                    const data = this.mapQuestionData(header, incidentIds);

                    this.excelService.fileName = fileName;
                    this.excelService.sheetName = this.surveyName;
                    this.excelService.title = this.surveyName;
                    this.excelService.header = header;
                    this.excelService.data = data;

                    this.excelService.generateExcelNewAsBlob().then(blob => {
                        observer.next({ blob, fileName: fileName.replace(/[ ]/gi, '_') + '.xlsx' });
                        observer.complete();
                    }).catch(err => {
                        console.error('[DownloadAsService] generateExcelForUpload - Excel generation failed:', err);
                        observer.error(err);
                    });
                },
                error: (error) => {
                    console.error('[DownloadAsService] generateExcelForUpload - getSurveyResponseData failed:', error);
                    observer.error(error);
                }
            });
        });
    }


    /**
     * Download resolution/dispute responses for multiple incidents
     */
    downloadDisputeFilesCombined(surveyId: string, items: { incidentId: number; disputeId: number }[]): Observable<boolean> {
        if (items == null || items.length === 0) {
            this.notificationService.showError("Please select at least one user to download the resolution responses.", "Error!");
            return of(false);
        }

        const incidentIds = items.map(i => i.incidentId);
        const disputeIds = items.map(i => i.disputeId).filter(id => id != null && id > 0);
        if (disputeIds.length === 0) {
            this.notificationService.showError("No resolution found for the selected response(s).", "Error!");
            return of(false);
        }

        this.spinnerService.show();
        return new Observable<boolean>(observer => {
            this.getSurveyResponseData(surveyId, incidentIds).subscribe({
                next: (result: any) => {
                    this.masterSurveyResults = result;
                    this.masterSurveyResults.surveyName = result.surveyDetail?.name;
                    this.surveyName = this.masterSurveyResults.surveyName;

                    // Fetch all disputes in parallel. take(1) ensures each source
                    // completes so forkJoin emits (disputeService.getDispute returns
                    // a Subject which never completes on its own).
                    const disputeCalls$ = disputeIds.map(id => this.disputeService.getDispute(id).pipe(take(1)));
                    forkJoin(disputeCalls$).subscribe({
                        next: (disputes: IDispute[]) => {
                            this.generateDisputeExcelForMultiple(disputes, incidentIds);
                            this.spinnerService.hide();
                            observer.next(true);
                            observer.complete();
                        },
                        error: () => {
                            this.spinnerService.hide();
                            observer.next(false);
                            observer.complete();
                        }
                    });
                },
                error: () => {
                    this.spinnerService.hide();
                    observer.next(false);
                    observer.complete();
                }
            });
        });
    }

    private generateDisputeExcelForMultiple(disputes: IDispute[], incidentIds: number[]): void {
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timestamp = this.utilityService.getFilenameTimestamp('en-US', userTimeZone);
        const fileName = `Resolution_Response_${this.surveyName}_${timestamp}`;

        const header = this.mapResolutionExportHeader();
        this.mapQuestionHeader(header, incidentIds);

        const normalRows = this.mapQuestionData(header, incidentIds, true);

        const resolutionRowByResponseId = new Map<number, any[]>();
        disputes.forEach(dispute => {
            const rows = this.mapDisputeQuestionData(header, incidentIds, dispute);
            if (rows.length > 0) {
                resolutionRowByResponseId.set(Number(dispute.surveyResponseId), rows[0]);
            }
        });

        const selectedResponses = (this.masterSurveyResults?.userResponses || [])
            .filter(x => incidentIds.includes(Number(x.surveyUserResponseRel)));

        const data: any[] = [];
        normalRows.forEach((normalRow: any[], idx: number) => {
            data.push(normalRow);
            const user = selectedResponses[idx];
            if (user) {
                const resolutionRow = resolutionRowByResponseId.get(Number(user.surveyUserResponseRel));
                if (resolutionRow) {
                    data.push(resolutionRow);
                }
            }
        });

        if (data.length === 0) {
            this.notificationService.showInfo("No resolution data available to download.", "Info!");
            return;
        }

        this.downloadExcel(fileName, header, data);
    }

    /**
     * Maps dispute response data into Excel rows aligned with the full form
     * header. Produces one row per selected incident, in the same order as
     * `mapQuestionData`, so each resolution row can be paired with its
     * corresponding normal response row. Dispute cells are populated only for
     * questions that exist in the dispute; all other question columns remain
     * blank to keep column alignment.
     */
    private mapDisputeQuestionData(header: IExcelOrExportDataObject[], incidentIds: number[], dispute: IDispute): any[] {
        // Index dispute questions by questionId for fast lookup.
        const disputeByQuestionId = new Map<number, IDisputeQuestion>();
        (dispute.disputeQuestions || []).forEach(dq => {
            disputeByQuestionId.set(dq.questionId, dq);
        });

        const selectedResponses = (this.masterSurveyResults?.userResponses || [])
            .filter(x => incidentIds.includes(Number(x.surveyUserResponseRel)));
        const indexToExclude = 3
        const quesHeaderList = header.filter((_, idx) => idx > indexToExclude);
        const rows: any[] = [];

        selectedResponses.forEach(user => {
            // Only emit a resolution row for the user whose surveyResponseId
            // matches this dispute. Other incidents have no resolution data.
            if (Number(user.surveyUserResponseRel) !== Number(dispute.surveyResponseId)) {
                return;
            }

            const row: any[] = this.mapCommonData(user,true,true);

            quesHeaderList.forEach(h => {
                if (h.subheaders.length > 0) {
                    // Grid/table: write the dispute cell once on the first
                    // sub-column and leave the remaining sub-columns blank so
                    // column count stays aligned with the header.
                    h.subQuestions.forEach((_, idx) => {
                        row.push(idx === 0 ? this.formatDisputeCell(disputeByQuestionId.get(h.questionId)) : '');
                    });
                } else {
                    row.push(this.formatDisputeCell(disputeByQuestionId.get(h.questionId)));
                }
            });

            rows.push(row);
        });

        return rows;
    }

    /**
     * Combines the dispute comments and attached file names into a single cell value.
     */
    private formatDisputeCell(dq: IDisputeQuestion | undefined): string {
        if (!dq) return '';
        const parts: string[] = [];
        if (dq.comments && dq.comments.trim().length > 0) {
            parts.push(dq.comments.trim());
        }
        const files = this.formatDisputeFileNames(dq.files);
        if (files) {
            parts.push(`Files: ${files}`);
        }
        return parts.join('\n');
    }

    /**
     * Parses the dispute file JSON string and returns a comma-separated list of file names.
     */
    private formatDisputeFileNames(filesJson: string): string {
        if (!filesJson) return '';
        try {
            const parsed = JSON.parse(filesJson);
            if (Array.isArray(parsed)) {
                return parsed.map(f => f?.name).filter(n => !!n).join(', ');
            }
        } catch {
            // Ignore parse errors and fall through.
        }
        return '';
    }

}
